
import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { ActionCardProps, ActionCardMediaItem } from '@/types/action-card';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronUp, Image, Play, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const MediaCard = ({ data, className }: ActionCardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeMedia, setActiveMedia] = useState(data.mediaItems?.[0]?.id || '');
  
  const renderMediaItem = (item: ActionCardMediaItem) => {
    switch(item.type) {
      case 'image':
        return (
          <div className="relative w-full h-full flex justify-center">
            <img 
              src={item.url} 
              alt={item.alt || item.title || "Image"} 
              className="rounded-md max-h-[300px] object-contain"
            />
          </div>
        );
      case 'video':
        return (
          <div className="relative w-full aspect-video">
            <video 
              src={item.url}
              controls
              className="w-full h-full rounded-md"
              poster={item.thumbnail}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      case 'audio':
        return (
          <div className="bg-white/5 dark:bg-gray-800/30 rounded-md p-4 flex items-center">
            <Volume2 className="h-8 w-8 text-primary mr-4" />
            <div className="flex-1">
              {item.title && <p className="font-medium mb-1">{item.title}</p>}
              <audio src={item.url} controls className="w-full" />
            </div>
          </div>
        );
      default:
        return <div>Unsupported media type</div>;
    }
  };

  const getIconForMediaType = (type: string) => {
    switch(type) {
      case 'image': return <Image className="h-4 w-4" />;
      case 'video': return <Play className="h-4 w-4" />;
      case 'audio': return <Volume2 className="h-4 w-4" />;
      default: return <Image className="h-4 w-4" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
      className={cn(
        "relative",
        "shadow-lg hover:shadow-xl transition-all duration-300",
        className
      )}
    >
      <Card className={cn(
        "backdrop-blur-sm bg-white/10 dark:bg-gray-900/20",
        "border border-white/20 dark:border-gray-800/30",
        "bg-gradient-to-br from-primary/10 to-primary/5",
        "p-4"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10 dark:border-gray-800/50">
          <div className="flex items-center gap-3">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 dark:bg-gray-800/30"
            >
              <Image className="h-4 w-4 text-primary" />
            </motion.div>
            <div className="space-y-0.5">
              <h3 className="text-lg font-semibold tracking-tight">{data.title}</h3>
              <p className="text-sm text-muted-foreground">{data.summary}</p>
            </div>
          </div>
          
          {data.isCollapsible && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 dark:hover:bg-gray-800/50 transition-colors"
              aria-label={isCollapsed ? "Expand card" : "Collapse card"}
            >
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Content */}
        <AnimatePresence>
          {!isCollapsed && data.mediaItems && data.mediaItems.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {/* Media display */}
              <div className="py-4">
                {/* Current active media */}
                {data.mediaItems.map(item => (
                  <div 
                    key={item.id} 
                    className={cn(
                      item.id === activeMedia ? "block" : "hidden"
                    )}
                  >
                    {renderMediaItem(item)}
                    {item.title && (
                      <p className="mt-2 font-medium text-center">{item.title}</p>
                    )}
                  </div>
                ))}
                
                {/* Media navigation - only show if we have more than 1 item */}
                {data.mediaItems.length > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {data.mediaItems.map(item => (
                      <Button
                        key={item.id}
                        variant={item.id === activeMedia ? "default" : "outline"}
                        size="icon"
                        className={cn(
                          "h-8 w-8 rounded-full",
                          item.id !== activeMedia && "bg-white/10 dark:bg-gray-800/30 border-white/10 dark:border-gray-800/30"
                        )}
                        onClick={() => setActiveMedia(item.id)}
                      >
                        {getIconForMediaType(item.type)}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Timestamp */}
        <div className="pt-2 text-[10px] text-muted-foreground">
          {data.timestamp}
        </div>
      </Card>
    </motion.div>
  );
};
